<template>
	<div class="p-component p-fluid p-formgrid p-grid">
		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ trainingKey.description }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<InputText type="text" v-model="trainingKey.value.value" disabled />
			<small v-if="!(trainingKey.value.value !== '' && trainingKey.value.value !== null)" class="p-error">{{$t("Squeeze.Training.TrainingKey")}}</small>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t('Squeeze.Training.Region') + ' ' + $t('Squeeze.Training.Anchor') }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="regionAnchorId"
				:options="allRegionAnchors"
				:optionLabel="getTranslatedDescription"
				optionValue="id"
				@change="getHeadFields"
			/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.DocumentClasses.Field") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="headField.fieldId"
				:options="documentFields"
				:optionLabel="getTranslatedDescription"
				optionValue="id"
				:filter="true"
				:autoFilterFocus="true"
				:loading="loading"
				@show="showDropdownOverlay"
			/>
		</div>

		<div class="p-col-3">
			<p v-tooltip.top="headField.locatorId ? $t('Squeeze.Training.OptionalFallback') : $t('Squeeze.Training.Optional')" style="font-size: 1rem; cursor: help;">{{ $t("Squeeze.Locators.Value") }}</p>
		</div>
		<div class="p-col-9">
			<div class="p-field p-input-filled p-inputgroup">
				<InputText
					type="text"
					v-model="headField.valuePattern"
					:placeholder="$t('Squeeze.Locators.Value')"
					@focus="onFocusField('headFieldValuePattern')"
				/>
				<Button class="active" icon="mdi mdi-auto-fix" @click="onFocusField('headFieldValuePattern')" />
			</div>
		</div>

		<div class="p-col-3">
			<p v-tooltip.top="$t('Squeeze.Training.Optional')" style="font-size: 1rem; cursor: help;">{{ $t("Squeeze.Training.Locator") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="headField.locatorId"
				:options="allLocators"
				optionLabel="description"
				optionValue="id"
				:showClear="true"
				:placeholder="$t('Squeeze.Training.SelectLocator')"
			/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.Region") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<div class="p-inputgroup">
				<span
					v-if="regionBadge"
					class="p-inputtext p-component region-display region-display--set"
				>✓ {{ regionBadge }}</span>
				<span
					v-else
					class="p-inputtext p-component region-display region-display--unset"
				>{{ $t('Squeeze.Training.RegionNotSet') }}</span>
				<Button icon="mdi mdi-vector-rectangle" @click="onFocusField('headFieldValueRegion')" />
			</div>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.IgnoreSpaces") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown v-model="headField.valueIgnoreWhitespace" :options="booleanOptions" optionLabel="name" optionValue="id" />
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.MultilineReadout") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown v-model="headField.accumulateFoundValues" :options="booleanOptions" optionLabel="name" optionValue="id" />
		</div>

		<div class="p-col-3">
			<span v-tooltip.top="!canTrain ? $t('Squeeze.Training.PatternOrLocatorRequired') : null">
				<Button
					:label="headField.id ? $t('Squeeze.Training.Retrain') : $t('Squeeze.Training.Train')"
					:disabled="!canTrain"
					@click="trainHeadField"
				/>
			</span>
		</div>
	</div><br/>

	<div class="inner-content">
		<FormHeadTrainingTable
			tableOrigin="headFields"
			:tableData="allHeadFields"
			:loading="loading"
			:documentFields="documentFields"
			@changeData="changeHeadField"
			@deleteData="deleteHeadField"
			@onMarkRegion="onMarkRegion"
		/>
	</div>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {defineComponent, onBeforeMount, onMounted, PropType, reactive, ref, watch, computed} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {
	DocumentField,
	FormTrainingHeadField,
	FormTrainingRegionAnchor,
	DocumentLocator
} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import {showDropdownOverlay} from "@/util/StylesHelper";
import FormHeadTrainingTable from "@/apps/squeeze/components/FormHeadTrainingTable.vue";

export default defineComponent({
	name: "FormTrainingHeadFields",
	methods: {showDropdownOverlay},
	components: {
		FormHeadTrainingTable,
		InputText,
		Dropdown,
		Button,
	},
	props: {
		trainingKeyField: {
			type: Object as PropType<DocumentField>,
			default: () => ({}),
		},
		documentClassId: {
			type: Number,
			required: true,
		},
		headFieldOfRegionInFormTraining: {
			type: Object as PropType<FormTrainingHeadField>,
			default: () => ({}),
		},
		documentFields: {
			type: Array as PropType<DocumentField[]>,
			default: () => [],
		},
		booleanOptions: {
			type: Array as PropType<any[]>,
			default: () => [],
		},
	},
	emits: [
		'onFocusField',
		'onMarkRegion',
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Current training key field */
		const trainingKey = reactive<DocumentField>({});

		/** Selected region anchor ID */
		const regionAnchorId = ref<number>(0);

		/** Current head field of region in form training */
		const headField = reactive<FormTrainingHeadField>({
			id: 0,
			fieldId: 0,
			valuePattern: '',
			valueRegion: {
				page: 0,
				x0: 0,
				y0: 0,
				x1: 0,
				y1: 0,
			},
			valueIgnoreWhitespace: false,
			accumulateFoundValues: false,
			locatorId: undefined,
		});

		/** Show Loading on load data */
		const loading = ref(false);

		/** All head fields */
		const allHeadFields = ref<FormTrainingHeadField[]>([]);

		/** All region anchors */
		const allRegionAnchors = ref<FormTrainingRegionAnchor[]>([]);

		/** All locators */
		const allLocators = ref<DocumentLocator[]>([]);

		/** Form Training API endpoint */
		const formTrainingApi = ClientManager.getInstance().squeeze.formTraining;

		/** Locator API endpoint */
		const locatorApi = ClientManager.getInstance().squeeze.locator;

		/**
		 * Triggered when a field is focused
		 * @param {string} fieldName
		 */
		const onFocusField = (fieldName: string) => {
			emit("onFocusField", fieldName);
		}

		/** Set region anchor ID */
		const setRegionAnchorId = () => {
			if (allRegionAnchors.value[0] && allRegionAnchors.value[0].id) {
				regionAnchorId.value = allRegionAnchors.value[0].id;
			}
		}

		/** Reset head field */
		const resetHeadField = () => {
			Object.assign(headField, {
				id: undefined,
				fieldId: 0,
				valuePattern: '',
				valueRegion: { page: 0, x0: 0, y0: 0, x1: 0, y1: 0 },
				valueIgnoreWhitespace: false,
				accumulateFoundValues: false,
				locatorId: undefined,
			});
		}

		/** Get all head fields */
		const getHeadFields = () => {
			loading.value = true;

			formTrainingApi.getFormHeadFields(props.documentClassId, regionAnchorId.value)
				.then(data => {
					allHeadFields.value = data;
					resetHeadField();
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					if (err.statusCode === 404) {
						allHeadFields.value = [];
					} else {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
					}
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Train head field */
		const trainHeadField = () => {
			loading.value = true;

			let promise;
			if (headField.id) {
				promise = formTrainingApi.retrainFormHeadField(props.documentClassId, headField.id, headField, true);
			} else {
				promise = formTrainingApi.trainFormHeadField(props.documentClassId, regionAnchorId.value, headField, false);
			}

			promise
				.then(() => {
					getHeadFields();
					resetHeadField();
				}).catch((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), err.message);
				}).finally(() => {
					loading.value = false;
				});
		}

		/**
		 * Change a head field
		 * @param data
		 */
		const changeHeadField = (data: FormTrainingHeadField) => {
			Object.assign(headField, { locatorId: undefined, ...data });
		}

		/**
		 * Deletes a head field
		 * @param data
		 */
		const deleteHeadField = (data: any) => {
			loading.value = true;
			formTrainingApi.deleteFormHeadField(props.documentClassId, data.id)
				.then(() => {
					resetHeadField();
					getHeadFields();
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Get all region anchors */
		const getAllRegionAnchors = () => {
			loading.value = true;

			formTrainingApi.getFormHeadRegionAnchors(props.documentClassId, trainingKey.value!.value!)
				.then(data => {
					allRegionAnchors.value = data;

					setRegionAnchorId();
					getHeadFields();
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					if (err.statusCode === 404) {
						allRegionAnchors.value = [];
					} else {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
					}
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Get all locators */
		const getAllLocators = () => {
			locatorApi.getAllLocators()
				.then(data => { allLocators.value = data; })
				.catch(response => response.json().then((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ': ' + err.message);
				}));
		}

		/** Computed badge string for current valueRegion */
		const regionBadge = computed(() => {
			const r = headField.valueRegion;
			if (!r || (r.x0 === 0 && r.y0 === 0 && r.x1 === 0 && r.y1 === 0)) return null;
			const fmt = (n: number | undefined) => Number((n ?? 0).toFixed(1));
			return `p.${r.page ?? 0} · (x0:${fmt(r.x0)}, y0:${fmt(r.y0)}) → (x1:${fmt(r.x1)}, y1:${fmt(r.y1)})`;
		});

		/** Whether all required fields are set to allow training */
		const canTrain = computed(() =>
			!!regionAnchorId.value
			&& !!headField.fieldId
			&& (headField.valuePattern !== '' || !!headField.locatorId)
			&& !!regionBadge.value
			&& trainingKey.value?.value !== ''
			&& trainingKey.value?.value != null
		);

		/**
		 * Set marker regions by clicking a row
		 * @param event
		 */
		const onMarkRegion = (event: {originalEvent: PointerEvent; data: any; index: number}) => {
			const row = event.data;
			emit("onMarkRegion", [row.valueRegion]);
		}

		onMounted(() => {
			Object.assign(headField, props.headFieldOfRegionInFormTraining);
			resetHeadField();
			getAllRegionAnchors();
			getAllLocators();
		})

		onBeforeMount(() => {
			Object.assign(trainingKey, props.trainingKeyField);
		})

		watch(
			() => [props.headFieldOfRegionInFormTraining.valuePattern, props.headFieldOfRegionInFormTraining.valueRegion],
			() => {
				headField.valuePattern = props.headFieldOfRegionInFormTraining.valuePattern;
				headField.valueRegion = props.headFieldOfRegionInFormTraining.valueRegion;
			},
			{ deep: true }
		)

		return {
			getTranslatedDescription,
			t,
			toast,
			trainingKey,
			regionAnchorId,
			headField,
			loading,
			allHeadFields,
			allRegionAnchors,
			allLocators,
			regionBadge,
			canTrain,
			onFocusField,
			getHeadFields,
			trainHeadField,
			changeHeadField,
			deleteHeadField,
			getAllRegionAnchors,
			getAllLocators,
			onMarkRegion,
		}
	},
});
</script>

<style scoped>

.inner-content {
	height: calc(100% - 32rem);
}

.region-display {
	background: var(--surface-ground);
	font-family: monospace;
	font-size: 0.85rem;
	display: flex;
	align-items: center;
}

.region-display--set {
	color: var(--green-400);
}

.region-display--unset {
	color: var(--red-400);
	font-style: italic;
}

</style>
